
import { computed, defineComponent, ref, watch, getCurrentInstance, onUnmounted } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMApp, SMCases, SMTasks, SMUser } from "@/store";
import type { API_TFile } from "@/models/file";
import { EMPLOYER_ROLE, EMPLOYEE_ROLE } from "@/constants/roles";

export default defineComponent({
  name: "Uploader",
  components: {
    NewPopup: () => import("@/modules/Documents/NewPopup.vue"),
    FileView: () => import("@/modules/Documents/FileView.vue"),
    PDFViewer: () => import("@/modules/Documents/PDFViewer.vue"),
  },
  props: {
    type: {
      type: String,
      // save_to_directory saves within the uploader into directory
      // task_template saves on finish task
      default: "save_to_directory",
    },
    directoryId: {
      type: Number,
      default: 0,
    },
    fileType: {
      type: String,
      default: "default",
    },
    name: {
      type: String,
      default: "Upload document",
    },
    parsingErrorMessage: {
      type: String,
      default: "",
    },
    parsingErrorMessageTooltip: {
      type: String,
      default: "",
    },
    savedFiles: { type: Array as () => API_TFile[], default: () => [] },
  },

  emits: [
    "open-parsed-document-popup",
    "cancel",
    "files",
    "set-additional-data",
    "set-date",
    "set-dates",
    "set-uploader-valid",
  ],

  setup(props, { emit }) {
    const router = getCurrentInstance()?.proxy?.$router;

    const taskTemplate = computed(() => SMTasks.taskTemplate);

    const MAX_SIZE_MB = 2 * 1024 * 1024; // 2MB in bytes

    const ACCEPTED_FILE_TYPE = "application/pdf";

    type UIFile = {
      raw: File; // original file
      name: string;
      size: number;
      type: string;
      url: string; // object URL for preview
      error?: string;
      errorTooltip?: string;
    };

    const rawFiles = ref<File[]>([]);

    const selectedFileIndex = ref(0);

    const fileInput = ref<HTMLInputElement | null>(null);

    const isDraggedOver = ref(false);

    const accept = computed(() => {
      return "application/pdf";
    });

    const fileType = computed(
      () => taskTemplate.value?.document_template_v2?.file_type || props.fileType
    );

    const isParsedFileType = computed(() => {
      return ["cos", "ielts", "pte"].includes(fileType.value);
    });

    const isMonthlyUploadFileType = computed(() => {
      return fileType.value === "monthly_upload";
    });

    const isDateFileType = computed(() => {
      return ["date_provided", "date_provided_previous_rtwc"].includes(
        fileType.value
      );
    });

    const isDateProvidedFileType = computed(() => {
      return ["date_provided"].includes(
        fileType.value
      );
    });

    const isDateProvidedLastRTWCFileType = computed(() => {
      return ["date_provided_last_rtwc"].includes(
        fileType.value
      );
    });

    const isDateProvidedPreviousRTWCFileType = computed(() => {
      return ["date_provided_previous_rtwc"].includes(
        fileType.value
      );
    });

    const isMultipleUpload = computed(() => {
      return !isParsedFileType.value;
    });

    const hasSaved = computed(() => !!props.savedFiles.length);

    const parsingError = ref<string>("");
    const parsingErrorTooltip = ref<string>("");

    const serverFileErrors = ref<Record<string, { error: string; errorTooltip?: string }>>({});

    function setServerFileErrors(errors: Record<string, { error: string; errorTooltip?: string }>) {
      serverFileErrors.value = errors;
    }

    function clearServerFileErrors() {
      serverFileErrors.value = {};
    }

    watch(
      () => [props.parsingErrorMessage, props.parsingErrorMessageTooltip],
      ([m, t]) => {
        parsingError.value = m || "";
        parsingErrorTooltip.value = t || "";
      },
      { immediate: true }
    );

    function validateFile(raw: File) {
      let error = "";
      let errorTooltip = "";

      if (raw.type !== ACCEPTED_FILE_TYPE) {
        error = "Upload only PDF files";
        errorTooltip = "This file format isn't recognised. Please upload only PDF files.";
      } else if (raw.size > MAX_SIZE_MB) {
        error = "Size must not exceed 2 MB";
        errorTooltip = `<b>The file's size must not exceed 2 MB. Please compress and reupload it</b>
          <ul style="list-style: decimal !important; margin-top: 4px; margin-left: 16px;">
            <li>Use <a href="https://www.ilovepdf.com/compress_pdf" target="_blank" style="text-decoration: underline;">this tool</a> to compress your file</li>
            <li>Reupload the compressed file by clicking on the 'Reupload' button</li>
          </ul>`;
      }

      if (parsingError.value) {
        error = parsingError.value;
        errorTooltip = parsingErrorTooltip.value;
      }

      const serverError = serverFileErrors.value[raw.name];
      if (serverError) {
        error = serverError.error;
        errorTooltip = serverError.errorTooltip;
      }

      return { error: error || undefined, errorTooltip: errorTooltip || undefined };
    }


    // URLs for preview
    const fileUrls = new WeakMap<File, string>();

    const getUrl = (f: File) => {
      let u = fileUrls.get(f);
      if (!u) { u = URL.createObjectURL(f); fileUrls.set(f, u); }
      return u;
    };

    const revokeUrl = (f: File) => {
      const u = fileUrls.get(f);
      if (u) { URL.revokeObjectURL(u); fileUrls.delete(f); }
    };


    const files = computed<UIFile[]>(() => {
      const seen = new Set<string>();

      return rawFiles.value.map((f) => {
        const id = `${f.name}-${f.size}`;
        const v = validateFile(f);
        if (seen.has(id) && !v.error) {
          v.error = "Uploaded twice";
          v.errorTooltip = "";
        }
        seen.add(id);

        return {
          raw: f,
          name: f.name,
          size: f.size,
          type: f.type,
          url: getUrl(f),
          ...v,
        };
      });
    });

    const showDropzone = computed(() => {
      if (hasSaved.value) return false;

      if (isParsedFileType.value && files.value.length) return false;

      return true;
    });

    // Date picker
    const date = ref(null);
    const startDate = ref(null);
    const endDate = ref(null);

    // Month Year Picker
    const currentYear = new Date().getFullYear();

    const yearOptions = computed(() => {
      return Array.from({ length: 12 }, (_, i) => {
        const yearValue = currentYear - 10 + i;
        return { value: yearValue.toString(), label: yearValue.toString() };
      });
    });

    const year = ref(currentYear.toString());

    const monthOptions = computed(() => {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return months.map((month) => ({
        value: month,
        label: month,
      }));
    });

    const month = ref(monthOptions.value[new Date().getMonth()].value);

    // File
    function setFileData() {
      if (isParsedFileType.value) {
        emit("open-parsed-document-popup", files.value.map(f => f.raw));

        return;
      }

      if (isMonthlyUploadFileType.value) {
        emit("set-additional-data", {
          year,
          month,
        });
      }

      if (isDateFileType.value) {
        emit("set-date", date);
      }

      if (isDateProvidedLastRTWCFileType.value) {
        emit("set-dates", {
          startDate: startDate.value,
          endDate: endDate.value,
        });
      }
    }

    watch([year, month], () => {
      if (!hasSaved.value && isMonthlyUploadFileType.value) setFileData();
    });

    watch(date, () => {
      if (!hasSaved.value && isDateFileType.value) setFileData();
    });

    watch([startDate, endDate], () => {
      if (!hasSaved.value && isDateProvidedLastRTWCFileType.value) setFileData();
    });

    watch(fileType, () => {
      if (!hasSaved.value) setFileData();
    });

    function selectFileIndex(index) {
      selectedFileIndex.value = index;
    }

    watch(rawFiles, (v) => {
      emit("files", v);
    }, { deep: false });

    const loading = ref(false);

    const hasError = computed(() => files.value.some(f => !!f.error));

    function onFiles(newRaw: File[]) {
      rawFiles.value = newRaw;

      setFileData();
    }

    // Compression
    async function compressFile(f: File): Promise<File | null> {
      try {
        const formData = new FormData();
        formData.append("file", f);

        const url = API_ROUTES.DOCUMENTS_FILES_COMPRESSION;

        const { data } = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });

        const blob = data as Blob;
        if (!(blob instanceof Blob)) return null;
        if (blob.size >= f.size) return f;

        return new File([blob], f.name, { type: "application/pdf" });
      } catch (e) {
        console.error("Compression failed:", e);

        return null;
      }
    }

    async function ensureCompressed(file: File): Promise<File> {
      // Only compress PDFs over the size limit
      const needsCompression =
        file.type === ACCEPTED_FILE_TYPE && file.size > MAX_SIZE_MB;

      if (!needsCompression) return file;

      const result = await compressFile(file);
      return result ?? file;
    }

    async function processIncomingFiles(incoming: File[], replaceIndex?: number | null) {
      clearServerFileErrors();
      loading.value = true;
      try {
        const compressed = await Promise.all(incoming.map(ensureCompressed));

        let next = rawFiles.value.slice();

        if (replaceIndex != null && replaceIndex >= 0 && replaceIndex < next.length) {
          const old = next[replaceIndex];
          if (old) revokeUrl(old);

          const newFile = compressed[0];
          if (!newFile) return;

          next = next.map((f, i) => (i === replaceIndex ? newFile : f));
          selectedFileIndex.value = replaceIndex;
        } else {
          next = [...next, ...compressed];
        }

        parsingError.value = "";
        parsingErrorTooltip.value = "";

        onFiles(next);
      } finally {
        loading.value = false;
      }
    }

    // Checks
    const checksComputed = computed(() => {
      const checksString = taskTemplate.value?.document_template_v2?.checks;

      if (!checksString) return [];

      if (hasSaved.value) return checksString.split(",,").map((label) => ({ label, checked: true }));

      return checksString.split(",,").map((label) => ({ label, checked: null }));
    });

    const checks = ref(checksComputed.value);

    const checkOptions = [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ];

    watch(
      () => props.savedFiles,
      (newFiles) => {
        if (!newFiles || newFiles.length === 0) return;

        checks.value = checksComputed.value;
      },
      { deep: true, immediate: true }
    );

    // Uploader
    function selectFile() {
      fileInput.value?.click();
    }

    function onFileReupload(index: number) {
      if (!fileInput.value) return;

      fileInput.value.setAttribute("data-index", index.toString());

      selectFile();
    }

    async function pickFile(e: Event) {
      const input = e.target as HTMLInputElement;
      if (!input.files?.length) return;

      const replaceIndex = input.dataset.index ? parseInt(input.dataset.index, 10) : null;
      const incoming = Array.from(input.files);

      if (replaceIndex !== null) {
        const newFile = incoming[0];
        if (!newFile) { input.value = ""; input.removeAttribute("data-index"); return; }
        await processIncomingFiles([newFile], replaceIndex);
        selectedFileIndex.value = replaceIndex;
      } else if (isMultipleUpload.value) {
        await processIncomingFiles(incoming, null);
      } else {
        // Single-file
        if (rawFiles.value[0]) revokeUrl(rawFiles.value[0]);
        await processIncomingFiles([incoming[0]], null);
        selectedFileIndex.value = 0;
      }

      input.value = "";
      input.removeAttribute("data-index");
    }

    const dragCounter = ref(0);

    async function onDrop(e: DragEvent) {
      e.preventDefault();
      dragCounter.value = 0;
      isDraggedOver.value = false;

      const fileList = e.dataTransfer?.files;
      if (!fileList?.length) return;

      if (isMultipleUpload.value) {
        await processIncomingFiles(Array.from(fileList), null);
      } else {
        if (rawFiles.value[0]) revokeUrl(rawFiles.value[0]);
        await processIncomingFiles([fileList[0]], null);
        selectedFileIndex.value = 0;
      }
    }

    function onDragEnter(e: DragEvent) {
      e.preventDefault();
      dragCounter.value++;
      isDraggedOver.value = true;
    }

    function onDragOver(e: DragEvent) {
      e.preventDefault();
    }

    function onDragLeave(e: DragEvent) {
      e.preventDefault();
      dragCounter.value--;

      if (dragCounter.value <= 0) {
        isDraggedOver.value = false;
      }
    }

    function formatBytes(bytes: number, decimals = 0) {
      if (!bytes) return "0 b";
      const k = 1024,
        sizes = ["b", "kb", "mb", "gb", "tb"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${(bytes / Math.pow(k, i)).toFixed(decimals)} ${sizes[i]}`;
    }

    const onFileDelete = (index: number) => {
      const target = rawFiles.value[index];
      if (!target) return;

      revokeUrl(target);

      onFiles(rawFiles.value.filter((_, i) => i !== index));

      emit("files", []);
    };

    // PDF Preview
    const fileIdViewer = ref(0);
    const pdfViewerKey = ref(0);
    const pdfViewerLink = ref("");
    const pdfViewerName = ref("");
    const isPdfViewerOpen = ref(false);

    function togglePdfViewer(value: boolean) {
      if (!value) {
        fileIdViewer.value = 0;
        selectedFileIndex.value = 0;
        pdfViewerLink.value = "";
        pdfViewerName.value = "";
        pdfViewerKey.value += 1;
      }

      isPdfViewerOpen.value = value;
    }

    async function onFilePreview(index: number) {
      const f = files.value[index];
      if (!f || f.type !== "application/pdf") return;
      pdfViewerLink.value = f.url;
      pdfViewerName.value = f.name;
      pdfViewerKey.value += 1;
      togglePdfViewer(true);
    }

    const showToggleFiles = computed(() => files.value.length > 1);

    const errorMessageShown = computed(() => !!files.value.length && checks.value.some(c => !c.checked));

    async function onToggleFile({ index, direction }: { index: number; direction: "prev" | "next" }) {
      let newIndex = index;
      if (direction === "next") newIndex = (index + 1) % files.value.length;
      else newIndex = (index - 1 + files.value.length) % files.value.length;

      const f = files.value[newIndex];
      if (!f) return;

      selectedFileIndex.value = newIndex;
      pdfViewerLink.value = f.url;
      pdfViewerName.value = f.name;
      pdfViewerKey.value += 1;
    }

    function onSavedFileView(fileId: number) {
      if (!fileId) return;

      fileIdViewer.value = fileId;

      pdfViewerKey.value += 1;

      togglePdfViewer(true);
    }

    // Validation
    const isUploaderValid = computed(() => {
      if (!files.value.length) return false;

      if (checks.value.some((check) => !check.checked)) return false;

      if (hasError.value) return false;

      return true;
    });

    watch(
      () => isUploaderValid.value,
      (valid) => {
        emit("set-uploader-valid", valid);
      },
      { deep: true, immediate: true }
    );

    // Go to documents
    function setPageSectionName() {
      const targetUser = SMTasks.taskTemplate?.target_user;

      if (!targetUser) return;

      const { first_name, last_name } = targetUser;

      const name = `${first_name} ${last_name}`;

      if (!name) return;

      SMApp.setEmployeeName(name);

      const sectionName =  `> ${name}`;

      SMApp.setPageSection(sectionName);
    }

    const COMPANY_PAGE_TITLE = "Company";
    const EMPLOYEES_PAGE_TITLE = "Employees";
    const MY_PROFILE_PAGE_TITLE = "My Profile";

    async function goToDocuments() {
      if (!props.savedFiles.length) return;

      const s3Path = props.savedFiles[0]?.s_3_path || "";
      if (!s3Path) return;

      const path = s3Path.replace(/\/[^/]*$/, "");

      let url = "";

      let object_type = "";

      if (taskTemplate.value.product_type == "case") {
        object_type = 'case';
      } else if (taskTemplate.value.product_type == "compliance") {
        if (taskTemplate.value.target_user.ID === 0) {
          if (taskTemplate.value.user.role == 'employer') {
            object_type = 'company';
          } else if (taskTemplate.value.user.role == 'employee') {
            object_type = 'employee';
          }
        } else {
          object_type = 'employee';
        }
      }

      try {
        if (object_type === "case") {
          url = `/cases/${SMCases.currentCaseId}/documents/${SMCases.documentId}`;
        } else if (SMUser.isEmployer) {
          if (
            SMTasks.taskTemplate?.target_user.role === EMPLOYEE_ROLE ||
            SMTasks.taskTemplate?.user_role === EMPLOYEE_ROLE
          ) {
            const id = SMTasks.taskTemplate?.target_user?.ID || SMTasks.taskTemplate?.user_id;
            if (!id) return console.log("No employee ID");

            url = `${SMUser.basePath}/employees/${id}/documents`;
            SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);
          } else {
            url = `${SMUser.basePath}/company/documents`;
            SMApp.setPageTitle(COMPANY_PAGE_TITLE);
          }
        } else if (SMUser.isEmployee) {
          url = `${SMUser.basePath}/profile/documents`;
          SMApp.setPageTitle(MY_PROFILE_PAGE_TITLE);
        } else if (SMUser.isExpert) {
          if (router.currentRoute.params.company_id) {
            if (
              SMTasks.taskTemplate?.target_user_id &&
              SMTasks.taskTemplate?.target_user.role !== EMPLOYER_ROLE
            ) {
              const { company_id } = router.currentRoute.params;
              url = `${SMUser.basePath}/companies/${company_id}/employees/${SMTasks.taskTemplate.target_user_id}/documents`;
              SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);
              setPageSectionName();
            } else {
              const { company_id } = router.currentRoute.params;
              url = `${SMUser.basePath}/companies/${company_id}/documents`;
              SMApp.setPageTitle(COMPANY_PAGE_TITLE);
            }
          } else {
            console.log("No company ID");
            return;
          }
        } else {
          console.log("Current role has no access!");
          return;
        }

        if (path) url += `?path=${encodeURIComponent(path)}`;

        await router.push(url);
      } catch (error) {
        console.error("Error navigating to documents:", error);
      }
    }

    onUnmounted(() => {
      rawFiles.value.forEach(revokeUrl);
    });

    return {
      setFileData,
      onFiles,
      hasSaved,
      fileInput,
      files,
      isDraggedOver,
      accept,
      selectFile,
      pickFile,
      onDrop,
      onDragOver,
      onDragEnter,
      onDragLeave,
      formatBytes,
      selectedFileIndex,
      onFileDelete,
      onFilePreview,
      onFileReupload,
      isParsedFileType,
      isMonthlyUploadFileType,
      isDateFileType,
      isDateProvidedFileType,
      isDateProvidedPreviousRTWCFileType,
      isDateProvidedLastRTWCFileType,
      checks,
      checkOptions,
      yearOptions,
      monthOptions,
      year,
      month,
      date,
      startDate,
      endDate,
      hasError,
      isMultipleUpload,
      selectFileIndex,
      showDropzone,
      errorMessageShown,
      isPdfViewerOpen,
      togglePdfViewer,
      fileIdViewer,
      pdfViewerKey,
      pdfViewerLink,
      pdfViewerName,
      loading,
      showToggleFiles,
      onToggleFile,
      goToDocuments,
      onSavedFileView,
      setServerFileErrors,
      clearServerFileErrors,
    };
  },
});
